<template>
  <div class="container-fluid">
    <b-form
      autocomplete="off"
      class="o-form"
      @submit="onSubmit"
      @reset="onReset"
    >
      <b-row class="pt-3 pb-3">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
            :disabled="form.nonEditable"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm="12"
          lg="4"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.enabled"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            :disabled="form.nonEditable"
            style="margin-right: 16px"
          />Enabled
        </b-col>
        <b-col />
        <b-col
          sm="12"
          md="4"
          lg="2"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.nonEditable"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            :hidden="true"
            style="margin-right: 16px"
          />
          <span v-if="form.nonEditable">Non Editable</span>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          class="my-2"
        >
          <b-form-group
            class="o-field"
            :state="nameState"
          >
            <label for="name">Name</label>
            <b-form-input
              id="name"
              v-model="form.name"
              :disabled="form.nonEditable"
              placeholder="Name"
              :state="nameState"
              trim
              @keyup="throttledMethod(validateUniqueName)"
            />
            <div class="invalid-tooltip">
              {{ nameInvalidFeedback }}
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          class="my-2"
        >
          <b-form-group class="o-field">
            <label for="displayName">Display Name</label>
            <b-form-input
              id="displayName"
              v-model="form.displayName"
              :disabled="form.nonEditable"
              placeholder="Display Name"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-show="showConsentFields">
        <b-col
          sm="12"
          md="3"
          lg="4"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.required"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Require Consent
        </b-col>
        <b-col
          sm="12"
          md="3"
          lg="4"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.emphasize"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Emphasize (Consent UI)
        </b-col>
        <b-col
          sm="12"
          md="3"
          lg="4"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.showInDiscoveryDocument"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Show in Discovery Document
        </b-col>
      </b-row>

      <!------------------------------ user claims ------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              :disabled="form.nonEditable"
              @click="addUserClaim()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addUserClaim"
              v-b-toggle.collapse-userClaims
              variant="link"
              :disabled="!hasUserClaims || form.nonEditable"
              class="px-3 py-0 o-btn-link o-strong"
            >
              User Claims&nbsp;
              <i
                v-if="hasUserClaims && !form.nonEditable"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasUserClaims"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-userClaims"
            v-model="collapseUserClaims"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(claim, claimIndex) in form.userClaims"
                :key="'claim' + claimIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'claim' + claimIndex"
                      class="text-truncate"
                    >Claim Type</label>
                    <b-form-input
                      :id="'claim' + claimIndex"
                      v-model="claim.type"
                      required
                      :disabled="form.nonEditable"
                      placeholder="Claim Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    :disabled="form.nonEditable"
                    @click="deleteUserClaim(claimIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!----------------------- Buttons ------------------------------>
      <b-row>
        <b-col>
          <b-alert
            v-model="showAlert"
            variant="danger"
            dismissible
          >
            {{ alertMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row class="pt-3 pb-3 justify-content-around">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
            :disabled="form.nonEditable"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
export default {
  data() {
    return {
      isNameUnique: true,
      showConsentFields: false,
      collapseUserClaims: false,
      form: {
        id: 0,
        name: "",
        displayName: "",
        nonEditable: false,
        emphasize: false,
        enabled: true,
        required: false,
        showInDiscoveryDocument: true,
        userClaims: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      isLoading: "isLoading",
    }),
    showAlert: {
      get() {
        return this.$store.getters.showAlert;
      },
      set(value) {
        this.$store.dispatch("showAlert", value);
      },
    },
    alertMessage: {
      get() {
        return this.$store.getters.alertMessage;
      },
      set(value) {
        this.$store.dispatch("alertMessage", value);
      },
    },
    hasUserClaims: function () {
      return this.form.userClaims.length > 0;
    },
    // Validation - Name Field
    nameState() {
      return this.form.name.length >= 4 && this.isNameUnique;
    },
    nameInvalidFeedback() {
      if (this.form.name.length < 4) {
        return "Enter at least 4 characters";
      } else if (!this.isNameUnique) {
        return "Must be unique across all Tenants";
      }
      return null;
    },
  },
  created() {
    this.loadIdentityResource();
  },
  methods: {
    loadIdentityResource() {
      if (this.$route.params.identityResourceId > 0) {
        this.$axios
          .get(
            `/api/identityResources/${this.$route.params.identityResourceId}`
          )
          .then((response) => {
            this.form = response.data;
            this.collapseUserClaims = this.hasUserClaims;
          })
          .catch((error) => {
            this.alertMesssage = error;
          });
      }
    },
    deleteUserClaim(claimIndex) {
      this.form.userClaims.splice(claimIndex, 1);
      this.collapseUserClaims = this.hasUserClaims;
    },
    addUserClaim() {
      if (this.form.userClaims === undefined) {
        this.form.userClaims = [];
      }
      this.form.userClaims.push({
        id: 0,
      });
      this.collapseUserClaims = this.hasUserClaims;
    },
    throttledMethod: function (func) {
      let debounced = _.debounce(func.bind(this), 500);
      debounced();
    },
    // validation
    validateUniqueName() {
      let value = this.form.name;
      if (value.length >= 4) {
        this.$axios
          .get(`/api/identityResources/${this.form.id}/isUnique/name/${value}`)
          .then((response) => {
            this.validationError = response.data;
            this.isNameUnique = response.data.valid;
            console.log("Result = " + this.isNameUnique);
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    onSubmit(evt) {
      evt.preventDefault();
      if (this.$route.params.identityResourceId == undefined) {
        this.createIdentityResource();
      } else {
        this.updateIdentityResource();
      }
    },
    createIdentityResource() {
      this.$axios
        .post("/api/identityResources/new", this.form)
        .then(() => {
          this.$router.push({ name: "identityResources" });
        })
        .catch((error) => {
          this.alertMesssage = error;
        });
    },
    updateIdentityResource() {
      this.$axios
        .put(
          `/api/identityResources/${this.$route.params.identityResourceId}/edit`,
          this.form
        )
        .then(() => {
          this.$router.push({ name: "identityResources" });
        })
        .catch((error) => {
          this.alertMesssage = error;
        });
    },
    onReset(evt) {
      evt.preventDefault();
      this.$router.push({ name: "identityResources" });
    },
  },
};
</script>
